import React, { useState, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { ViewModelForm, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { CoverablesService, CoverablesUtil } from 'gw-capability-codegen';
import PropTypes from 'prop-types';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import styles from './CoverablesStyles.module.scss';
import {
    useCoverable,
    useCoverages,
    useExposures,
    useRefState,
    useAccordionStates
} from '../../hooks';

function Coverable(props) {
    const { authHeader } = useAuthentication();
    const {
        onSaveCoverable,
        coverableData,
        callbackProps: {
            coverableType,
            owningCoverablePublicID
        },
        viewModelService,
        addCoverableMetadata,
        lobName,
        sessionUUID,
        jobID
    } = props;
    const [coverableVM, setCoverableVM] = useState({});
    const [isCoveragesInitialised, onCoveragesInitialised] = useState(false);
    const [initialCoverableSnapshot, setInitialCoverableSnapshot] = useState();
    const [, updateState] = useState();
    const [isSaving, updateIsSaving, isSavingRef] = useRefState(false);
    const { isComponentValid, onValidate } = useValidation('Coverable');

    useEffect(() => {
        const dtoName = CoverablesUtil
            .getCoverableDefinition(lobName, coverableType).dtoname;
        const newVm = viewModelService.create(coverableData, 'pc', dtoName);
        setCoverableVM(newVm);
        setInitialCoverableSnapshot(newVm.value);
    },
    [viewModelService, props, coverableType, owningCoverablePublicID, lobName, coverableData]);

    const onModelChange = (value) => {
        setCoverableVM(value);
        updateState({});
    };

    const {
        saveCoverable,
        handleFieldBlur
    } = useCoverable({
        coverableVM,
        coverableType,
        jobID,
        sessionUUID,
        owningCoverablePublicID,
        lobName,
        updateIsSaving,
        isSavingRef,
        authHeader
    });

    useEffect(() => {
        const saveCoverableOnLoad = async () => {
            if (coverableVM && coverableVM.aspects && coverableVM.aspects.valid
                && coverableVM.aspects.subtreeValid) {
                if (!isCoveragesInitialised) {
                    await saveCoverable();
                    onCoveragesInitialised(true);
                }
            }
        };
        saveCoverableOnLoad();
    }, [coverableVM.aspects]);

    const {
        updateCoverage,
        syncCoverages,
        updateAndSyncCoverages,
        loadingClause,
    } = useCoverages({
        lobName,
        ownerVM: coverableVM,
        coverableVM,
        coverableType,
        jobID: jobID,
        sessionUUID: sessionUUID,
        updateIsSaving,
        clausesBasePath: 'coverages',
        authHeader
    });

    const {
        addExposure,
        deleteExposure
    } = useExposures({
        ownerVM: coverableVM
    });

    const {
        openAccordions,
        accordionCallbackProps,
        renderKey
    } = useAccordionStates({
        ownerVM: coverableVM
    });

    const addNewExposure = useCallback((callbackProps) => {
        addExposure(callbackProps);
        openAccordions(callbackProps)
    }, [addExposure, ]);

    const handleSave = async () => {
        await saveCoverable();
        onSaveCoverable(true, coverableVM.value);
    };

    const undoChanges = async () => {
        const isEditingExistingCoverable = _.get(initialCoverableSnapshot, 'publicID');
        if (isEditingExistingCoverable) {
            await saveCoverable(initialCoverableSnapshot);
        } else if (coverableVM.value.publicID) {
            await CoverablesService
                .callMethod(lobName, coverableType, 'remove', [
                    jobID,
                    sessionUUID,
                    coverableVM.value
                ], authHeader);
        }
    };

    const handleBack = useCallback(
        async () => {
            await undoChanges();
            onSaveCoverable(false);
        }, [undoChanges, onSaveCoverable]
    );

    const overrideProps = {
        '@field': {
            onBlur: handleFieldBlur
        },
        coverages: {
            visible: isComponentValid,
            loadingClause
        },
        conditions: {
            visible: isComponentValid,
            loadingClause
        },
        exclusions: {
            visible: isComponentValid,
            loadingClause
        },
        infoBox: {
            visible: !isComponentValid
        },
        ...accordionCallbackProps()
    };

    overrideProps[`${coverableType}AddButton`] = { disabled: isSaving || !isComponentValid };

    const callbackMap = {
        onChangeClause: updateCoverage,
        onSyncCoverages: syncCoverages,
        onChangeSubmissionAndSync: updateAndSyncCoverages,
        onSave: handleSave,
        onBack: handleBack,
        addExposure: addNewExposure,
        deleteExposure,
        onValidate
    };

    return (
        <div key={renderKey()}>
            <ViewModelForm
                uiProps={addCoverableMetadata.content}
                model={coverableVM}
                onModelChange={onModelChange}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                callbackMap={callbackMap}
                classNameMap={styles}
            />
        </div>
    );
}
Coverable.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func.isRequired,
        clone: PropTypes.func.isRequired
    }).isRequired,
    jobID: PropTypes.string.isRequired,
    sessionUUID: PropTypes.string.isRequired,
    addCoverableMetadata: PropTypes.shape({
        content: PropTypes.shape({}).isRequired
    }).isRequired,
    onSaveCoverable: PropTypes.func.isRequired,
    callbackProps: PropTypes.shape({
        path: PropTypes.string.isRequired,
        coverableType: PropTypes.string.isRequired,
        owningCoverablePublicID: PropTypes.string
    }).isRequired,
    coverableData: PropTypes.shape({}),
    lobName: PropTypes.string.isRequired
};

Coverable.defaultProps = {
    coverableData: {}
};

export default withViewModelService(Coverable);
